// client/src/components/ticketing/ClaimTicketPage.jsx
//
// Public, unauthenticated ticket-claim page — Phase 9 ("send tickets to
// individuals"). Reached via /ticket-invite/:claimToken from the invite
// email. Explicit page-state machine modeled on EventLandingPage.jsx (which
// itself follows ExternalUploadPage.jsx's established pattern for a
// standalone public route): loading/ready/claimed/expired/not_found/error.
// Renders WITHOUT AppHeader/Sidebar/AppFooter, same as EventLandingPage.

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import CustomField from './CustomField';
import MyTicket from './MyTicket';

const STATE_LOADING = 'loading';
const STATE_READY = 'ready';
const STATE_CLAIMED = 'claimed';
const STATE_UNAVAILABLE = 'unavailable'; // expired or revoked
const STATE_NOT_FOUND = 'not_found';
const STATE_ERROR = 'error';

function formatDateRange(startdate, enddate) {
  if (!startdate) return '';
  try {
    const start = new Date(startdate);
    const end = enddate ? new Date(enddate) : null;
    const opts = { dateStyle: 'medium', timeStyle: 'short' };
    const startLabel = start.toLocaleString(undefined, opts);
    if (!end) return startLabel;
    return `${startLabel} – ${end.toLocaleString(undefined, opts)}`;
  } catch (err) {
    console.warn('formatDateRange: could not parse date', err);
    return startdate;
  }
}

export default function ClaimTicketPage() {
  const { claimToken } = useParams();
  const [pageState, setPageState] = useState(STATE_LOADING);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const [name, setName] = useState('');
  const [attributes, setAttributes] = useState({});
  const [busy, setBusy] = useState(false);
  const [claimResult, setClaimResult] = useState(null); // { tickets, accessToken }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: resp } = await axios.get(`/api/ticketing/public/invite/${claimToken}`);
        if (cancelled) return;
        setData(resp);
        setName(resp.invite?.recipientName || '');
        if (resp.status === 'pending') setPageState(STATE_READY);
        else if (resp.status === 'claimed') setPageState(STATE_CLAIMED);
        else setPageState(STATE_UNAVAILABLE);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404) setPageState(STATE_NOT_FOUND);
        else {
          setError(err?.response?.data?.message || 'Something went wrong loading this invite.');
          setPageState(STATE_ERROR);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [claimToken]);

  const requiredFieldsFilled = useMemo(() => {
    if (!data) return false;
    if (!name.trim()) return false;
    for (const field of data.fields || []) {
      if (!field.required) continue;
      const v = attributes[field.field_key];
      if (v === undefined || v === null || v === '' || v === false) return false;
    }
    return true;
  }, [data, name, attributes]);

  const setAttribute = (key, value) => {
    setAttributes((a) => ({ ...a, [key]: value }));
  };

  const submitClaim = async () => {
    setBusy(true);
    setError('');
    try {
      const { data: resp } = await axios.post(`/api/ticketing/public/invite/${claimToken}/claim`, {
        name: name.trim(),
        attributes,
      });
      if (resp?.success) {
        setClaimResult({ tickets: resp.tickets || [] });
        setPageState(STATE_CLAIMED);
      } else {
        setError(resp?.message || 'Could not claim this invite');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not claim this invite');
    } finally {
      setBusy(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100">
      <div className="max-w-2xl mx-auto px-4 py-10">{children}</div>
    </div>
  );

  if (pageState === STATE_LOADING) {
    return shell(<div className="text-sm opacity-70">Loading invite…</div>);
  }

  if (pageState === STATE_NOT_FOUND) {
    return shell(
      <div className="text-center">
        <h1 className="text-xl font-semibold mb-2">Invite not found</h1>
        <p className="text-sm opacity-70">Double-check the link, or ask whoever sent it to resend.</p>
      </div>
    );
  }

  if (pageState === STATE_UNAVAILABLE) {
    return shell(
      <div className="text-center">
        <h1 className="text-xl font-semibold mb-2">This invite is no longer available</h1>
        <p className="text-sm opacity-70">
          It may have expired or been revoked. Ask whoever sent it to send a new one.
        </p>
      </div>
    );
  }

  if (pageState === STATE_ERROR) {
    return shell(
      <div className="text-center">
        <h1 className="text-xl font-semibold mb-2">Something went wrong</h1>
        <p className="text-sm opacity-70">{error}</p>
      </div>
    );
  }

  if (pageState === STATE_CLAIMED) {
    // Two different moments look the same status-wise but need different UI:
    // claimResult is set right after THIS visit submitted the claim (show the
    // full ticket list), whereas an invite the GET already reported as
    // 'claimed' was claimed on a previous visit — nothing to show here but a
    // link back to My Tickets.
    if (!claimResult) {
      return shell(
        <div className="text-center">
          <h1 className="text-xl font-semibold mb-2">Already claimed</h1>
          <p className="text-sm opacity-70 mb-4">These tickets have already been claimed.</p>
          {data.myTicketsUrl && (
            <a href={data.myTicketsUrl} className="underline">View your tickets</a>
          )}
        </div>
      );
    }
    return shell(
      <>
        <h1 className="text-2xl font-bold mb-4">{data.event?.name}</h1>
        <MyTicket
          tenantId={data.tenant?.id}
          tickets={claimResult.tickets}
          pendingWebhook={false}
          hideBuyMore
        />
      </>
    );
  }

  // STATE_READY
  const { tenant, event, tier, item, fields, invite } = data;
  return shell(
    <>
      <div className="flex items-center gap-3 mb-6">
        {tenant?.logo && (
          <img src={tenant.logo} alt={tenant.companyname} className="w-10 h-10 rounded object-contain bg-white" />
        )}
        <span className="font-semibold opacity-80">{tenant?.companyname}</span>
      </div>

      <h1 className="text-2xl font-bold mb-1">You've been sent tickets!</h1>
      <div className="text-sm opacity-70 mb-4">
        {invite.numTickets} ticket{invite.numTickets === 1 ? '' : 's'} to <strong>{event?.name}</strong>
        {item?.name && item.name !== event?.name ? ` — ${item.name}` : ''} ({tier?.name})
      </div>
      <div className="text-sm opacity-70 mb-2">{formatDateRange(event?.startdate, event?.enddate)}</div>

      {invite.message && (
        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 my-4 italic text-sm">
          "{invite.message}"
        </div>
      )}

      {event?.description && <p className="mb-6 mt-4 opacity-90">{event.description}</p>}

      <h2 className="font-semibold mb-2">Confirm your details</h2>
      <input
        className="w-full px-3 py-2 rounded border bg-transparent mb-3"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />

      {fields.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          {fields.map((field) => (
            <CustomField key={field.id} field={field} value={attributes[field.field_key]} onChange={(v) => setAttribute(field.field_key, v)} />
          ))}
        </div>
      )}

      {error && <div className="text-red-600 dark:text-red-400 text-sm mb-3">{error}</div>}

      <button
        type="button"
        onClick={submitClaim}
        disabled={busy || !requiredFieldsFilled}
        className="w-full px-4 py-2 rounded bg-cobalt text-white font-semibold disabled:opacity-50"
      >
        {busy ? 'Claiming…' : `Claim your ticket${invite.numTickets === 1 ? '' : 's'}`}
      </button>
      {!requiredFieldsFilled && (
        <p className="mt-2 text-xs opacity-60">Complete the required fields above to claim.</p>
      )}
    </>
  );
}
